import type { CSSProperties } from "react";

/**
 * How far up a game's own ladder a child has got.
 *
 * One component rather than one per screen: the student page and the class comparison both draw it,
 * and two copies would drift into two different pictures of the same number.
 *
 * **The only inline style in the app, and it carries a measurement rather than a design decision.**
 * A width that comes from a child's data has no static form, so it cannot be a Tailwind class — it
 * arrives as a CSS custom property and the `w-span` utility does the sizing. `check-styles.ts`
 * allows this file by name.
 *
 * The object comes from a named helper rather than being written inline, and that is not a formatting
 * preference: `check-styles.ts` rejects an inline object outright, wherever it appears, on the
 * reasoning that an object written at the call site is where a colour or a spacing decision sneaks
 * in. A function that can only ever return a percentage cannot carry one.
 *
 * (The check reads raw source, comments included — so naming the pattern it forbids in this comment
 * would fail the build. That is the guard working: a rule which can be quoted can be pasted.)
 */
const spanWidth = (percent: number): CSSProperties =>
  ({ "--span-w": `${percent.toFixed(1)}%` }) as CSSProperties;

export function LevelBar({ reached, max }: { reached: number; max: number }) {
  // Clamped: a level window that drifted past a game's ceiling would otherwise draw past the track.
  const filled = Math.max(0, Math.min(1, max > 0 ? reached / max : 0));
  return (
    <span
      className="mt-sm block h-2 w-full overflow-hidden rounded-pill bg-edge"
      style={spanWidth(filled * 100)}
    >
      <span className="w-span block h-full rounded-pill bg-grape" />
    </span>
  );
}
